// Dashboard de pipeline comercial — /crm/pipeline (Story 11.12, FR-54)
// - 1 request consolidado (cards + funil + conversão por origem + a vencer)
// - Filtros globais (período + responsável) afetam TODOS os widgets (AC6),
//   persistidos em URL query params
// - RBAC no front: ações rápidas só pra admin/comercial; backend já mascara
//   valores (suporte) e conversão (financeiro/suporte) — AC8
// - Skeleton no loading, error state com retry, empty states por widget

import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';

import type { PipelinePeriod } from '@control-center/shared';
import { pipelinePeriodSchema } from '@control-center/shared';

import { PageHelp } from '../../components/common/PageHelp';
import { PillButton } from '../../components/clients/PillButton';
import { Select } from '../../components/clients/form-primitives';
import { ConversionByOriginChart } from '../../components/crm/pipeline/ConversionByOriginChart';
import { ExpiringProposalsTable } from '../../components/crm/pipeline/ExpiringProposalsTable';
import { FunnelChart } from '../../components/crm/pipeline/FunnelChart';
import { PipelineCards } from '../../components/crm/pipeline/PipelineCards';
import { useCrmPipelineSummary } from '../../hooks/use-crm-pipeline';
import { useUsers } from '../../hooks/use-users';
import { useCrmPermissions } from '../../hooks/use-crm-permissions';
import { extractErrorMessage } from '../../lib/errors';

const PERIOD_OPTIONS: { value: PipelinePeriod; label: string }[] = [
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
  { value: 'all', label: 'Tudo' },
];

/** Lê o período da URL (default 30d), validando contra o enum do schema. */
function parsePeriod(raw: string | null): PipelinePeriod {
  const result = pipelinePeriodSchema.safeParse(raw);
  return result.success ? result.data : '30d';
}

export function CrmPipelinePage(): JSX.Element {
  const { canWrite: canAct } = useCrmPermissions();

  const [searchParams, setSearchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'));
  const responsibleUserId = searchParams.get('responsibleUserId') ?? '';

  const { data: users } = useUsers();
  const userNames = useMemo(() => {
    const map = new Map<string, string>();
    (users ?? []).forEach((u) => map.set(u.id, u.name));
    return map;
  }, [users]);

  const { data, isLoading, isError, error, refetch, isFetching } = useCrmPipelineSummary(
    period,
    responsibleUserId || undefined,
  );

  // Garante que a URL sempre tenha um período explícito (rota compartilhável).
  useEffect(() => {
    if (!searchParams.get('period')) {
      const next = new URLSearchParams(searchParams);
      next.set('period', period);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setParam = (key: string, value: string): void => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Breadcrumb + header (AC10) */}
      <nav className="text-xs text-muted-3" aria-label="breadcrumb">
        CRM <span className="mx-1 text-muted-3">›</span>
        <span className="text-muted">Pipeline</span>
      </nav>

      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="m-0 text-[30px] font-bold leading-none tracking-[-0.025em] text-ink">
              Pipeline Comercial
            </h1>
            <p className="mt-[6px] max-w-[560px] text-[13px] leading-[1.5] text-muted">
              Funil, propostas a vencer e conversão por origem — visibilidade real do pipeline, sem
              planilha.
            </p>
          </div>
          <PageHelp id="crm-pipeline" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            aria-label="Filtrar por responsável"
            value={responsibleUserId}
            onChange={(e) => setParam('responsibleUserId', e.target.value)}
          >
            <option value="">Todos os responsáveis</option>
            {(users ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
          <PillButton
            onClick={() => refetch()}
            icon={
              <RefreshCw
                size={14}
                strokeWidth={2}
                className={isFetching ? 'animate-spin' : undefined}
              />
            }
          >
            Atualizar
          </PillButton>
        </div>
      </header>

      {/* Período segmentado (AC6) */}
      <div className="inline-flex w-fit rounded-[10px] border border-line bg-panel p-1">
        {PERIOD_OPTIONS.map((opt) => {
          const active = opt.value === period;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setParam('period', opt.value)}
              aria-pressed={active}
              className={`rounded-[8px] px-3 py-1 text-[13px] transition-colors ${
                active ? 'bg-panel-2 font-medium text-ink' : 'text-muted hover:text-ink'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* Conteúdo */}
      {isLoading ? (
        <PipelineSkeleton />
      ) : isError ? (
        <ErrorState
          message={extractErrorMessage(error, 'Não foi possível carregar o dashboard.')}
          onRetry={() => refetch()}
        />
      ) : data ? (
        <div className="flex flex-col gap-5">
          <PipelineCards summary={data.summary} />

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <FunnelChart
              funnel={data.funnel}
              showMoney={data.summary.total_in_negotiation !== null}
            />
            {data.conversion_by_origin !== null ? (
              <ConversionByOriginChart rows={data.conversion_by_origin} />
            ) : (
              <RestrictedWidget title="Conversão por origem" />
            )}
          </div>

          <ExpiringProposalsTable
            proposals={data.proposals_expiring}
            userNames={userNames}
            canAct={canAct}
          />
        </div>
      ) : null}
    </div>
  );
}

// =============================================================================
// Estados auxiliares
// =============================================================================

function PipelineSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-[110px] animate-pulse rounded-lg border border-line bg-panel-2"
          />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="h-80 animate-pulse rounded-[14px] border border-line bg-panel-2" />
        <div className="h-80 animate-pulse rounded-[14px] border border-line bg-panel-2" />
      </div>
      <div className="h-64 animate-pulse rounded-[14px] border border-line bg-panel-2" />
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-bad/40 bg-panel p-6">
      <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">
        Não foi possível carregar o dashboard.
      </h2>
      <p className="mt-1 text-[12px] text-muted">{message} Tente novamente.</p>
      <div className="mt-4">
        <PillButton onClick={onRetry} icon={<RefreshCw size={14} strokeWidth={2} />}>
          Tentar de novo
        </PillButton>
      </div>
    </div>
  );
}

/** Widget oculto por role (financeiro não vê conversão por origem — AC8). */
function RestrictedWidget({ title }: { title: string }): JSX.Element {
  return (
    <section className="flex flex-col overflow-hidden rounded-[14px] border border-line bg-panel">
      <header className="border-b border-line px-4 py-3">
        <h3 className="text-sm font-medium text-ink">{title}</h3>
      </header>
      <div className="flex flex-1 items-center justify-center py-10 text-center text-sm text-muted">
        Indicador restrito ao seu perfil de acesso.
      </div>
    </section>
  );
}
